import { useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, PlusIcon, StarIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { useContacts, useCreateContact } from '../../hooks/useContacts';
import type { Contact } from '../../services/api/contacts';
import type { LeadContact } from '../../services/api/leads';

/**
 * MKS-344 — pick the contacts on a lead.
 *
 * Search the existing ones (by name, e-mail or company, server-side) or
 * create one inline. Inline creation is a real `POST /api/contacts` followed
 * by linking the returned id — the same two-step the Company combobox above
 * already uses for an unknown customer, so the lead write stays a pure link
 * operation and the new contact is immediately reusable on the next deal.
 *
 * `customerId` is the lead's company: it prefills the new contact's company,
 * which is what makes the e-mail dedupe in the backfill (and in people's
 * heads) line up with what the picker creates.
 */
interface LeadContactsPickerProps {
  value: LeadContact[];
  onChange: (next: LeadContact[]) => void;
  /** The lead's company, used to prefill an inline-created contact. */
  customerId: number | null;
}

/** A freshly created contact, shaped as it appears on a lead. */
function toLeadContact(contact: Contact, isPrimary: boolean): LeadContact {
  return {
    id: contact.id,
    name: contact.name,
    email: contact.email,
    phone: contact.phone,
    customerId: contact.customerId,
    customer: contact.customer,
    isPrimary,
  };
}

export function LeadContactsPicker({ value, onChange, customerId }: LeadContactsPickerProps) {
  const [filter, setFilter] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const createContactMutation = useCreateContact();
  // Only fetch while the dropdown is open: the lead form already issues half a
  // dozen catalogue queries on mount and the picker is not always touched.
  const { data: contactsData } = useContacts(isOpen ? { search: filter } : {});
  const available = (contactsData?.data ?? []).filter(
    (c) => !value.some((picked) => picked.id === c.id),
  );

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const trimmedFilter = filter.trim();
  const canCreate =
    trimmedFilter !== '' &&
    !available.some((c) => c.name.toLowerCase() === trimmedFilter.toLowerCase()) &&
    !value.some((c) => c.name.toLowerCase() === trimmedFilter.toLowerCase());

  /** Keep exactly one primary: the first contact when none is flagged. */
  const withPrimary = (contacts: LeadContact[]): LeadContact[] => {
    if (contacts.length === 0) return contacts;
    const primaryIndex = contacts.findIndex((c) => c.isPrimary);
    const chosen = primaryIndex === -1 ? 0 : primaryIndex;
    return contacts.map((c, i) => ({ ...c, isPrimary: i === chosen }));
  };

  const addContact = (contact: Contact) => {
    onChange(withPrimary([...value, toLeadContact(contact, value.length === 0)]));
    setFilter('');
    setNewEmail('');
    setNewPhone('');
    setIsOpen(false);
  };

  const removeContact = (id: number) => {
    onChange(withPrimary(value.filter((c) => c.id !== id)));
  };

  const makePrimary = (id: number) => {
    onChange(value.map((c) => ({ ...c, isPrimary: c.id === id })));
  };

  const createInline = async () => {
    if (!trimmedFilter) return;
    try {
      const created = await createContactMutation.mutateAsync({
        name: trimmedFilter,
        // The lead's company, so the new contact is filed where it belongs
        // without the user picking it a second time.
        customerId,
        email: newEmail.trim() || null,
        phone: newPhone.trim() || null,
      });
      addContact(created.data);
    } catch {
      // The mutation's own error toast already reported it.
    }
  };

  return (
    <div className="col-span-2" ref={dropdownRef}>
      <label htmlFor="lead-contacts" className="block text-sm font-medium text-gray-700 mb-1">
        Contacts
      </label>

      {value.length > 0 && (
        <ul className="mb-2 space-y-1">
          {value.map((contact) => (
            <li
              key={contact.id}
              className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5"
            >
              <button
                type="button"
                onClick={() => makePrimary(contact.id)}
                aria-label={
                  contact.isPrimary ? `${contact.name} is the primary contact` : `Make ${contact.name} primary`
                }
                aria-pressed={contact.isPrimary}
                title={contact.isPrimary ? 'Primary contact' : 'Make primary'}
                className={`shrink-0 ${contact.isPrimary ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
              >
                {contact.isPrimary ? (
                  <StarSolidIcon className="w-4 h-4" />
                ) : (
                  <StarIcon className="w-4 h-4" />
                )}
              </button>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm text-gray-900">{contact.name}</p>
                {/* The modal is where someone actually reaches the person, so
                    the e-mail and phone are live mailto:/tel: links rather
                    than text to copy by hand. */}
                {(contact.email || contact.phone) && (
                  <p className="truncate text-xs text-gray-500">
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {contact.email}
                      </a>
                    )}
                    {contact.email && contact.phone && ' · '}
                    {contact.phone && (
                      <a
                        href={`tel:${contact.phone}`}
                        className="text-primary-600 hover:text-primary-700"
                      >
                        {contact.phone}
                      </a>
                    )}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => removeContact(contact.id)}
                aria-label={`Remove ${contact.name}`}
                className="shrink-0 p-0.5 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="relative">
        <input
          id="lead-contacts"
          type="text"
          value={filter}
          onChange={(e) => {
            setFilter(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          placeholder="Search contacts or type a new name..."
          className="w-full rounded-lg border border-gray-300 px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle contacts list"
          className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
        >
          <ChevronDownIcon className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-64 overflow-auto">
            {available.map((contact) => (
              <button
                key={contact.id}
                type="button"
                onClick={() => addContact(contact)}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50"
              >
                <span className="block truncate">{contact.name}</span>
                {(contact.email || contact.customer?.name) && (
                  <span className="block truncate text-xs text-gray-400">
                    {[contact.email, contact.customer?.name].filter(Boolean).join(' · ')}
                  </span>
                )}
              </button>
            ))}

            {canCreate && (
              <div className="border-t border-gray-100 p-2 space-y-2">
                <p className="text-xs text-gray-500">
                  New contact &ldquo;{trimmedFilter}&rdquo; — e-mail and phone are optional.
                </p>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="E-mail (optional)"
                  aria-label="New contact e-mail"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <input
                  type="tel"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="Phone (optional)"
                  aria-label="New contact phone"
                  className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <button
                  type="button"
                  onClick={createInline}
                  disabled={createContactMutation.isPending}
                  className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm text-green-700 hover:bg-green-50 disabled:opacity-50"
                >
                  <PlusIcon className="w-4 h-4" />
                  Add &ldquo;{trimmedFilter}&rdquo;
                </button>
              </div>
            )}

            {available.length === 0 && !canCreate && (
              <p className="px-3 py-2 text-sm text-gray-400">No contacts found</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default LeadContactsPicker;
